/**
 * Unlock: turn the family code back into the family key.
 *
 * Reached when a parent is signed in, belongs to a family, and this device
 * has no key for it -- a new device, a cleared browser, or a private window.
 *
 * Signed in and unlocked are two different states on purpose. Cognito proves
 * who you are to the API; the code proves you can read what the API stores.
 * The server can enforce the first and has no way to enforce the second,
 * which is the point.
 *
 * THIS SCREEN USED TO BE A DEAD END, and the fix is the whole reason the
 * family code exists. It asked for a four-word phrase this account had been
 * shown once, months earlier. A parent who no longer had it was already a
 * member, so they never saw the join screen and there was nowhere to redeem
 * anything; the only honest answer was "ask another parent to invite you
 * again", which is a strange thing to need from your own household. Now it
 * asks for the one code the whole family shares, which any other member can
 * read off their own Settings screen in ten seconds.
 *
 * FORGOT THE CODE (2026-10-04). The footnote below used to be the end of the
 * road: ask somebody whose device is unlocked to read the code out. Now this
 * device can ask that device directly, and be let in without anybody reading
 * anything -- see family/keyRequests.ts. Once in, it shows the code it now
 * can, and offers a new one, which is the "reset": every unlocked device
 * reads the new code from then on, and the old one stops working.
 */

import { Loading } from '../components/Spinner';
import { useEffect, useState } from 'react';
import {
  formatFamilyCode,
  joinFamily,
  readFamilyCode,
  rotateFamilyCode,
  type FamilySummary,
} from '../family/families';
import {
  askForKey,
  checkKeyRequest,
  myKeyRequest,
  withdrawKeyRequest,
  type AskedForKey,
} from '../family/keyRequests';
import { loadState } from '../family/state';
import { Secret } from '../components/Secret';
import { StartOver } from './StartOver';

/** How often the asking screen checks for an answer. */
const ASK_POLL_MS = 4000;


export function Unlock({
  family,
  onUnlocked,
  onSignOut,
}: {
  family: FamilySummary;
  onUnlocked: (key: CryptoKey) => void;
  onSignOut: () => void;
}) {
  const [code, setCode] = useState('');
  const [asking, setAsking] = useState(false);
  /** A request this device made earlier, found again after signing back in. */
  const [earlier, setEarlier] = useState<AskedForKey | null>(null);
  const [startingOver, setStartingOver] = useState(false);
  const [recovered, setRecovered] = useState<CryptoKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Asked from this device before and signed out? Go straight back to it.
  // (A yes is collected before this screen is ever shown -- see App.)
  useEffect(() => {
    let stop = false;
    void myKeyRequest(family.id)
      .then((mine) => {
        if (stop || !mine) return;
        if (mine.status === 'approved') setRecovered(mine.key);
        else {
          setEarlier(mine.asked);
          setAsking(true);
        }
      })
      .catch(() => {});
    return () => {
      stop = true;
    };
  }, [family.id]);

  function attempt(work: () => Promise<void>): void {
    setError('');
    setBusy(true);
    void work()
      .catch((problem: unknown) => {
        setError(problem instanceof Error ? problem.message : 'That did not work.');
      })
      .finally(() => setBusy(false));
  }

  if (recovered) {
    return (
      <Recovered familyId={family.id} familyKey={recovered} onDone={() => onUnlocked(recovered)} />
    );
  }

  if (startingOver) {
    return (
      <StartOver familyId={family.id} onDone={onUnlocked} onBack={() => setStartingOver(false)} />
    );
  }

  if (asking) {
    return (
      <AskAnotherDevice
        familyId={family.id}
        earlier={earlier}
        onApproved={setRecovered}
        onBack={() => {
          setEarlier(null);
          setAsking(false);
        }}
        onSignOut={onSignOut}
      />
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        attempt(async () => {
          const opened = await joinFamily(code);
          onUnlocked(opened.key);
        });
      }}
    >
      <h2>Enter your family code</h2>
      <p>
        Your money, goals and journal are already here. Your family&rsquo;s
        names and characters need the family code before this device can read
        them.
      </p>

      <p className="field-label">Family code</p>
      <Secret value={code} typed onChange={setCode} />
      <p className="footnote">
        Any family member can find it under Settings &rarr; Family code. It is
        the same code for everybody, and it is not your sign-in password.
        Capitals and dashes do not matter.
      </p>

      {error ? <p className="error">{error}</p> : null}

      <div className="actions">
        <button aria-busy={busy} className="primary" type="submit" disabled={busy || !code}>
          {busy ? 'Unlocking…' : 'Unlock'}
        </button>
        <button type="button" disabled={busy} onClick={onSignOut}>
          Sign out
        </button>
      </div>

      <section className="forgot">
        <h3>Forgot the code?</h3>
        <p>
          If Birbly is open on another phone or computer &mdash; yours, or
          another parent&rsquo;s &mdash; that device can let this one in. You
          can make a new code once you are in.
        </p>
        <button type="button" disabled={busy} onClick={() => setAsking(true)}>
          Ask another device to let me in
        </button>
        <p className="footnote">
          Nobody has the code and no device can let you in?{' '}
          <button type="button" className="text-button" disabled={busy} onClick={() => setStartingOver(true)}>
            Start over with a new key
          </button>{' '}
          &mdash; keeps every balance and the history, but not the names.
        </p>
      </section>

      <p className="footnote">
        We cannot show you the code: what we store is your family&rsquo;s key
        locked with it, and a hash we cannot reverse. Another member&rsquo;s
        unlocked device can. If nobody has one, the names and characters are
        gone &mdash; though the ledger, the goals and every balance survive
        untouched, because none of them is ever encrypted.
      </p>
    </form>
  );
}

/**
 * The asking side: one request, its one-time code, and a wait.
 *
 * Polls every few seconds while the screen is up. Leaving does NOT withdraw
 * the request: it lasts a week and this device finds it again after signing
 * back in, so a parent can read the code out, sign out, and come back once
 * somebody has typed it. "Cancel this request" is the way to take it back.
 */
function AskAnotherDevice({
  familyId,
  earlier,
  onApproved,
  onBack,
  onSignOut,
}: {
  familyId: string;
  earlier: AskedForKey | null;
  onApproved: (key: CryptoKey) => void;
  onBack: () => void;
  onSignOut: () => void;
}) {
  const [asked, setAsked] = useState<AskedForKey | null>(earlier);
  const [state, setState] = useState<'asking' | 'waiting' | 'denied' | 'expired'>(
    earlier ? 'waiting' : 'asking',
  );
  const [error, setError] = useState('');

  useEffect(() => {
    if (earlier) return;
    let stop = false;
    // Asking twice from this device returns the same request, so a double
    // run here (React's StrictMode) cannot leave a second one behind.
    void askForKey(familyId)
      .then((made) => {
        if (stop) return;
        setAsked(made);
        setState('waiting');
      })
      .catch((problem: unknown) => {
        if (!stop) setError(problem instanceof Error ? problem.message : 'That did not work.');
      });
    return () => {
      stop = true;
    };
  }, [familyId, earlier]);

  useEffect(() => {
    if (!asked || state !== 'waiting') return;
    let stop = false;
    const timer = window.setInterval(() => {
      void checkKeyRequest(familyId, asked.id)
        .then((outcome) => {
          if (stop) return;
          if (outcome.status === 'approved') onApproved(outcome.key);
          else if (outcome.status === 'denied') setState('denied');
        })
        .catch((problem: unknown) => {
          // A 404 is a request that ran out of time; anything else is a
          // dropped poll, and the next one is a few seconds away.
          if (!stop && (problem as { status?: number }).status === 404) setState('expired');
        });
    }, ASK_POLL_MS);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [asked, state, familyId, onApproved]);

  function cancel() {
    if (asked && state === 'waiting') void withdrawKeyRequest(familyId, asked.id).catch(() => {});
    onBack();
  }

  return (
    <div>
      <h2>Ask another device</h2>
      {error ? <p className="error">{error}</p> : null}
      {state === 'asking' && !error ? <Loading label="Asking your other device…" /> : null}
      {asked && state === 'waiting' ? (
        <>
          <p>
            Give this code to a parent whose Birbly is already unlocked. They
            type it under <strong>Settings &rarr; Members</strong>, or say yes
            when their Home screen asks.
          </p>
          <p className="field-label">Your one-time code</p>
          <p className="match-code" aria-live="polite">{asked.code}</p>
          <p className="footnote">
            It works once, for this device and this account, for a week. You
            can sign out now: once they have typed it, sign in here again and
            you will be let straight in. If you stay on this screen, it carries
            on by itself.
          </p>
        </>
      ) : null}
      {state === 'denied' ? (
        <p className="error">The other device said no. If that was a mistake, ask again.</p>
      ) : null}
      {state === 'expired' ? (
        <p className="error">Nobody answered in time. Ask again when the other device is open.</p>
      ) : null}
      <div className="actions">
        {state === 'denied' || state === 'expired' ? (
          <button
            className="primary"
            type="button"
            onClick={() => {
              setAsked(null);
              setState('asking');
              setError('');
              void askForKey(familyId)
                .then((made) => {
                  setAsked(made);
                  setState('waiting');
                })
                .catch((problem: unknown) =>
                  setError(problem instanceof Error ? problem.message : 'That did not work.'),
                );
            }}
          >
            Ask again
          </button>
        ) : null}
        <button type="button" onClick={onBack}>
          Back
        </button>
        {asked && state === 'waiting' ? (
          <>
            <button type="button" onClick={onSignOut}>
              Sign out
            </button>
            <button type="button" className="text-button" onClick={cancel}>
              Cancel this request
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * In. Here is the code this device can now read -- and the reset.
 *
 * A new code is the same rotation Settings does: the family KEY does not
 * change, so every device already unlocked stays unlocked and shows the new
 * code from then on, and children's devices never used the code at all.
 * What stops working is the old code, and any join link sent with it.
 */
function Recovered({
  familyId,
  familyKey,
  onDone,
}: {
  familyId: string;
  familyKey: CryptoKey;
  onDone: () => void;
}) {
  const [current, setCurrent] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void readFamilyCode(familyId, familyKey)
      .then(setCurrent)
      .catch(() => setCurrent(null));
  }, [familyId, familyKey]);

  async function makeNew() {
    setBusy(true);
    setError('');
    try {
      const { familyName } = await loadState(familyId, familyKey);
      setFresh(await rotateFamilyCode(familyId, familyKey, familyName));
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2>{fresh ? 'Your new family code' : 'This device is unlocked'}</h2>
      {fresh ? (
        <>
          <Secret value={formatFamilyCode(fresh)} label="Your new family code" />
          <p className="footnote">
            The old code has stopped working. Every phone and computer already
            unlocked stays unlocked and shows this one under Settings &rarr;
            Family code. Children&rsquo;s devices are not affected.
          </p>
        </>
      ) : (
        <>
          {current ? <Secret value={formatFamilyCode(current)} label="Your family code" /> : null}
          <p>
            {current
              ? 'That is your family code. You can find it again any time under Settings → Family code.'
              : 'You are in.'}{' '}
            If you think somebody who should not have it does, make a new one:
            the old one stops working straight away.
          </p>
        </>
      )}
      {error ? <p className="error">{error}</p> : null}
      <div className="actions">
        <button className="primary" type="button" disabled={busy} onClick={onDone}>
          Done
        </button>
        {fresh ? null : (
          <button aria-busy={busy} type="button" disabled={busy} onClick={() => void makeNew()}>
            {busy ? 'Making one…' : 'Make a new family code'}
          </button>
        )}
      </div>
    </div>
  );
}
